export const CHECKOUT_URL = 'https://erectil-en.impultienda.ar/checkout/'

export const PRODUCT_IMAGE =
  'https://cdn.magicpatterns.com/uploads/uJhSwcUkXietia6icbK3m7/ERECTIL-EN.png'

export const AUTHOR_IMAGE =
  'https://cdn.magicpatterns.com/uploads/kn2jTBLJR8nQwoJj9uAgPo/Dra_Karito_real.png'

export const stats = [
  {
    value: '30+',
    label: 'READY-TO-USE CLINICAL DECISION ALGORITHMS',
    description:
      'Practical flowcharts designed to help you navigate the most common erectile dysfunction scenarios.',
  },
  {
    value: '20+',
    label: 'CLINICAL CASES & REAL-WORLD SCENARIOS',
    description:
      'Step-by-step clinical cases designed to help you apply the decision pathways to different patient profiles.',
  },
  {
    value: '40+',
    label: 'CONSULTATION TOOLS & CLINICAL CHECKLISTS',
    description:
      'Ready-to-use worksheets, assessment checklists, follow-up tools, decision guides, and communication resources.',
  },
  {
    value: '100%',
    label: 'STRUCTURED AROUND THE CLINICAL DECISION PROCESS',
    description: 'Evaluate → Identify → Decide → Act → Monitor',
  },
]

export const clinicalContextFactors = [
  'Cardiovascular factors',
  'Metabolic factors',
  'Hormonal factors',
  'Medications',
  'Psychological factors',
  'Lifestyle',
  'Or a combination of several of them',
]

export const routeSteps = [
  {
    number: '1',
    title: 'Identify the Clinical Scenario',
    text: 'Determine what you need to understand first.',
  },
  {
    number: '2',
    title: 'Follow the Decision Path',
    text: 'Move through the relevant clinical checkpoints.',
  },
  {
    number: '3',
    title: 'Determine the Next Step',
    text: 'Organize the information you need before moving forward.',
  },
]

export const beforeItems = [
  'Consultations that begin without a clear structure',
  'Uncertainty about what may be contributing to erectile dysfunction',
  'Ordering tests without a clear framework for when they may actually add value',
  'Treating the symptom without considering the patient’s broader clinical context',
  'A patient returns because the initial approach did not produce the expected response',
  'Difficult conversations about sexual health',
]

export const withRouteItems = [
  'A structured pathway to guide the initial evaluation.',
  'A structured framework to consider relevant vascular, metabolic, hormonal, medication-related, psychological, and lifestyle factors.',
  'Decision tools to organize what information may be relevant according to the clinical scenario.',
  'A structured approach that helps you evaluate the patient beyond the presenting complaint.',
  'A follow-up framework to reassess response, adherence, tolerance, contributing factors, and next steps.',
  'Communication frameworks designed to help you address sensitive questions with greater clarity and professionalism.',
]

export const painPoints = [
  {
    emoji: '😰',
    text: 'A patient presents with difficulty achieving or maintaining an erection, and you’re not sure where to begin the evaluation.',
  },
  {
    emoji: '⏱️',
    text: 'You spend too much time reviewing different clinical resources before feeling confident that you have considered the relevant factors.',
  },
  {
    emoji: '🤷',
    text: 'You’re unsure whether cardiovascular, metabolic, hormonal, medication-related, or psychological factors should change your clinical approach.',
  },
  {
    emoji: '🧪',
    text: 'You wonder which laboratory tests may actually contribute useful information for this particular patient.',
  },
  {
    emoji: '😓',
    text: 'Your patient asks: “Doctor, could this be because my testosterone is low?” or “Is this a circulation problem?” — and you need a structured way to guide the conversation without losing the clinical focus.',
  },
  {
    emoji: '😩',
    text: 'The patient returns because the initial approach did not produce the expected result, and you have to reconstruct the entire clinical picture again.',
  },
]

export type Volume = {
  roman: string
  index: string
  title: string
  promise: string
  intro: string[]
  includes: string[]
}

export const volumes: Volume[] = [
  {
    roman: 'I',
    index: 'VOLUME I',
    title: 'The Starting Point',
    promise:
      'Build a structured first evaluation for the patient with erectile dysfunction.',
    intro: [
      'Before thinking about treatment, you need to understand what you’re actually evaluating.',
      'This volume organizes the essential information for the initial consultation and helps you build a focused clinical history.',
    ],
    includes: [
      'Focused medical history',
      'Characterization of erectile dysfunction',
      'Symptom assessment',
      'Relevant medical history',
      'Medication review',
      'Lifestyle factors',
      'Initial clinical assessment',
      'Differential considerations',
      'Initial evaluation algorithms',
      'Clinical cases',
    ],
  },
  {
    roman: 'II',
    index: 'VOLUME II',
    title: 'Discover What’s Behind the Problem',
    promise:
      'Identify the major clinical factors that may be associated with erectile dysfunction.',
    intro: [
      'Erectile dysfunction can appear within a much broader clinical context.',
      'This volume organizes the key areas to consider during your evaluation.',
    ],
    includes: [
      'Cardiovascular factors',
      'Metabolic factors',
      'Hormonal factors',
      'Medication-related factors',
      'Psychological factors',
      'Lifestyle factors',
      'Relevant medical history',
      'Clinical warning signs',
      'Risk assessment frameworks',
      'Decision algorithms',
    ],
  },
  {
    roman: 'III',
    index: 'VOLUME III',
    title: 'The Evaluation Pathway',
    promise:
      'Stop asking, “What should I evaluate next?” and start following a structured pathway.',
    intro: [
      'This volume provides visual decision maps designed to help you organize the evaluation according to the patient’s clinical scenario.',
    ],
    includes: [
      'Clinical flowcharts',
      'Decision trees',
      'Evaluation checkpoints',
      'Common patient scenarios',
      'Factors that modify the clinical approach',
      'Laboratory considerations',
      'Structured interpretation frameworks',
      'Clinical algorithms',
      'Practical cases',
    ],
  },
  {
    roman: 'IV',
    index: 'VOLUME IV',
    title: 'The Action Plan',
    promise:
      'Organize the clinical approach according to the characteristics and needs of the patient.',
    intro: [
      'The goal isn’t to memorize one universal answer.',
      'The goal is to structure the decision around the individual clinical context.',
    ],
    includes: [
      'Initial management framework',
      'Factors influencing treatment decisions',
      'Therapeutic options',
      'Individualization principles',
      'Patient education',
      'Adherence considerations',
      'Common mistakes',
      'Initial follow-up',
      'Decision checklists',
    ],
  },
  {
    roman: 'V',
    index: 'VOLUME V',
    title: 'When the Case Isn’t That Simple',
    promise:
      'Navigate the scenarios that require a more careful clinical evaluation.',
    intro: [
      'Patients with multiple contributing factors. Insufficient response. Comorbidities. Medications. Hormonal concerns. Psychological contributors.',
      'Situations where the initial approach does not produce the expected result.',
    ],
    includes: [
      'Complex cases',
      'Inadequate response',
      'Relevant comorbidities',
      'Associated factors',
      'Special clinical scenarios',
      'Reassessment frameworks',
      'Problem-solving algorithms',
      'Clinical case studies',
    ],
  },
  {
    roman: 'VI',
    index: 'VOLUME VI',
    title: 'Consultation Ready',
    promise:
      'Everything you need to turn the Clinical Route into practical consultation tools.',
    intro: [],
    includes: [
      'Consultation checklists',
      'Clinical templates',
      'Patient assessment forms',
      'Follow-up worksheets',
      'Clinical protocols',
      'Documentation tools',
      'Communication scripts',
      'Patient goal sheets',
      'Quick-reference tools',
    ],
  },
]

export type Bonus = {
  label: string
  title: string
  hook: string
  description: string
  bullets?: string[]
  value: string
}

export const bonuses: Bonus[] = [
  {
    label: 'BONUS #1',
    title: 'Erectile Dysfunction Treatment Reference Guide™',
    hook: 'Quickly review key treatment options and their clinical considerations.',
    description:
      'A practical reference resource designed to organize information about commonly used treatment approaches, relevant considerations, and clinical decision points.',
    value: '$19',
  },
  {
    label: 'BONUS #2',
    title: 'Labs Without the Confusion™',
    hook: 'Organize laboratory evaluation according to the clinical scenario.',
    description:
      'A practical framework for understanding when laboratory information may contribute to your assessment and when it may not necessarily change your clinical approach.',
    value: '$25',
  },
  {
    label: 'BONUS #3',
    title: 'Clinical Scripts for Difficult Patient Questions™',
    hook: 'Practical conversation frameworks for the questions patients really ask.',
    description:
      'Discuss sensitive questions about sexual health, expectations, risks, and treatment options with greater clarity and professionalism.',
    bullets: [
      '“Doctor, is this because my testosterone is low?”',
      '“Is this a circulation problem?”',
      '“Will I need treatment forever?”',
      '“Does this mean something is seriously wrong?”',
    ],
    value: '$29',
  },
  {
    label: 'BONUS #4',
    title: 'The Inadequate Response Decision Algorithms™',
    hook: 'What should you do when the patient comes back and says: “It didn’t work.”',
    description:
      'A collection of decision maps to help structure reassessment.',
    bullets: [
      'What changed?',
      'What factors should be reviewed?',
      'Are there adherence issues?',
      'Are there contributing factors?',
      'What information is missing?',
      'When should the patient be reassessed?',
      'What should be considered next?',
    ],
    value: '$35',
  },
  {
    label: 'BONUS #5',
    title: 'The Professional Erectile Dysfunction Consultation Kit™',
    hook: 'Bring the Clinical Route directly into your consultation.',
    description: 'Everything ready to print and use with the patient in front of you.',
    bullets: [
      'First consultation checklist',
      'Specialized history template',
      'Patient assessment worksheet',
      'Follow-up checklist',
      'Patient goals worksheet',
      'Progress note template',
      'Patient communication guide',
    ],
    value: '$39',
  },
]

export const valueStack = [
  {
    name: 'Clinical Route for Erectile Dysfunction™ — 6 Complete Volumes',
    value: '$197',
  },
  { name: 'Erectile Dysfunction Treatment Reference Guide™', value: '$19' },
  { name: 'Labs Without the Confusion™', value: '$25' },
  { name: 'Clinical Scripts for Difficult Patient Questions™', value: '$29' },
  { name: 'Inadequate Response Decision Algorithms™', value: '$35' },
  { name: 'Professional Erectile Dysfunction Consultation Kit™', value: '$39' },
]

export const accessIncludes = [
  '6 Complete Clinical Volumes',
  '30+ Decision Algorithms',
  '20+ Clinical Cases & Scenarios',
  '40+ Consultation Tools & Checklists',
  '5 Exclusive Bonuses',
  'Digital Download',
  'Lifetime Access',
  '7-Day Guarantee',
]

export const methodSteps = [
  { code: '01', title: 'EVALUATE', text: 'Build a focused clinical picture.' },
  { code: '02', title: 'IDENTIFY', text: 'Recognize relevant contributing factors.' },
  { code: '03', title: 'DECIDE', text: 'Use the appropriate decision pathway.' },
  { code: '04', title: 'ACT', text: 'Structure the next clinical step.' },
  {
    code: '05',
    title: 'MONITOR',
    text: 'Reassess the patient and determine what needs attention next.',
  },
]

export const withoutSystem = [
  'Search.',
  'Compare.',
  'Second-guess.',
  'Open another guideline.',
  'Search again.',
  'Try to remember what you read.',
]

export const withSystem = [
  'Identify the scenario.',
  'Open the relevant pathway.',
  'Follow the checkpoints.',
  'Review the clinical considerations.',
  'Document and monitor.',
]

export const faqs = [
  {
    question: 'Is access immediate?',
    answer:
      'Yes. Once your payment is confirmed, you will receive access to the digital materials.',
  },
  {
    question: 'Is this a physical product?',
    answer:
      'No. The Clinical Route is delivered digitally and is designed to be used on your computer, tablet, or phone, with printable resources included where appropriate.',
  },
  {
    question: 'Is this suitable for physicians in different countries?',
    answer:
      'The system is designed as an educational clinical decision-support resource based on widely used international clinical evidence. However, recommendations, approved treatments, prescribing rules, and clinical guidelines can vary by country. Always apply your own professional judgment and the regulations and guidelines applicable to your practice.',
  },
  {
    question: 'Is this a one-time payment?',
    answer:
      'Yes. You pay $27 USD once. There are no monthly subscriptions or recurring charges.',
  },
  {
    question: 'Will the material be updated?',
    answer:
      'Clinical evidence evolves over time. Any future updates should be clearly identified and reviewed against current evidence before being incorporated into clinical practice.',
  },
  {
    question: 'Does this replace clinical judgment?',
    answer:
      'No. Clinical Route for Erectile Dysfunction™ is an educational decision-support resource. It does not replace professional medical judgment, individualized patient assessment, current clinical guidelines, or applicable local regulations.',
  },
]

export const testimonials = [
  {
    name: 'Dr. Andrés Molina',
    role: 'Family Medicine · Colombia',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/416ca0d2-68a7-4dc2-b497-e844d14c44dc.jpg',
    quote:
      'I used to improvise the first consultation. Now I open the pathway, follow the checkpoints, and finish with a clear plan and a documented follow-up.',
  },
  {
    name: 'Dra. Valentina Ruiz',
    role: 'Internal Medicine · Mexico',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e112fb5e-3360-4cb4-9b3c-a070fb55bc0d.jpg',
    quote:
      'The algorithms saved me from jumping between four guidelines. In ten minutes I know what matters for THIS patient and what does not.',
  },
  {
    name: 'Dr. Ricardo Peña',
    role: 'Urology · Argentina',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/f28272c1-85fc-49e6-9bdb-fd802d4db8b4.jpg',
    quote:
      'The reassessment framework for inadequate response is what I use the most. I no longer rebuild the whole clinical picture from zero.',
  },
  {
    name: 'Dra. Camila Herrera',
    role: 'General Practice · Chile',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/c44ea6c8-feb6-4a57-890a-6554feea8d2f.jpg',
    quote:
      'The communication scripts changed my consultations. Sensitive questions no longer take me off the clinical track.',
  },
  {
    name: 'Dr. Julián Vargas',
    role: 'Family Medicine · Peru',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/9c52c3b7-c40d-487d-bfd2-c7529e53aa06.jpg',
    quote:
      'Printable checklists in the consultation room. That single detail made the system part of my daily routine.',
  },
]

export const buyerNotifications = [
  { name: 'Dr. Martín Gómez', place: 'Buenos Aires, Argentina' },
  { name: 'Dra. Lucía Fernández', place: 'Madrid, Spain' },
  { name: 'Dr. Carlos Mendoza', place: 'Lima, Peru' },
  { name: 'Dra. Sofía Ramírez', place: 'Bogotá, Colombia' },
  { name: 'Dr. Javier Ortega', place: 'Mexico City, Mexico' },
  { name: 'Dra. Paula Navarro', place: 'Santiago, Chile' },
  { name: 'Dr. Esteban Rojas', place: 'San José, Costa Rica' },
  { name: 'Dra. Mariana Silva', place: 'Montevideo, Uruguay' },
  { name: 'Dr. Andrés Castillo', place: 'Quito, Ecuador' },
  { name: 'Dra. Daniela Torres', place: 'Guayaquil, Ecuador' },
  { name: 'Dr. Rafael Duarte', place: 'Asunción, Paraguay' },
  { name: 'Dra. Gabriela Suárez', place: 'Caracas, Venezuela' },
  { name: 'Dr. Iván Salazar', place: 'La Paz, Bolivia' },
  { name: 'Dra. Rocío Delgado', place: 'Barcelona, Spain' },
  { name: 'Dr. Hugo Medina', place: 'Panama City, Panama' },
]

export const evidenceSources = ['AUA', 'EAU', 'ISSM', 'ESH', 'ESC']
